import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";

/**
 * Checks if a time slot is bookable for the given guest count
 */
const isTimeBookable = (time: TimeSlot, guestCount: number): boolean => {
    return time.availableSeats.includes(guestCount);
};

/**
 * Checks if a time slot is available for waitlist for the given guest count
 */
const isTimeWaitListable = (time: TimeSlot, guestCount: number): boolean => {
    return time?.waitListSeats?.includes(guestCount) ?? false;
};

/**
 * Filters time slots to only include those that are bookable for the given guest count
 */
const getBookableTimes = (times: TimeSlot[], guestCount: number): TimeSlot[] => {
    return times.filter((time) => isTimeBookable(time, guestCount));
};

/**
 * Filters time slots to only include those that are waitlistable for the given guest count
 */
const getWaitListTimes = (times: TimeSlot[], guestCount: number): TimeSlot[] => {
    return times.filter((time) => isTimeWaitListable(time, guestCount));
};

/**
 * Filters a time set to only include bookable times for the given guest count
 */
const getBookableTimeSet = (timeSet: WebRule, guestCount: number): WebRule => {
    return {
        ...timeSet,
        times: getBookableTimes(timeSet.times, guestCount),
    } as WebRule;
};

/**
 * Filters a time set to only include waitlistable times for the given guest count
 */
const getWaitListTimeSet = (timeSet: WebRule, guestCount: number): WebRule => {
    return {
        ...timeSet,
        times: getWaitListTimes(timeSet.times, guestCount),
    } as WebRule;
};

/**
 * Filters multiple time sets to only include bookable times for the given guest count
 * Removes any time sets that end up with no times
 */
const getBookableTimeSets = (timeSets: WebRule[], guestCount: number): WebRule[] => {
    return timeSets
        .map((timeSet) => new WebRule(getBookableTimeSet(timeSet, guestCount)))
        .filter((timeSet) => timeSet.times.length > 0);
};

/**
 * Filters multiple time sets to only include waitlistable times for the given guest count
 * Removes any time sets that end up with no times
 */
const getWaitListTimeSets = (timeSets: WebRule[], guestCount: number): WebRule[] => {
    return timeSets
        .map((timeSet) => new WebRule(getWaitListTimeSet(timeSet, guestCount)))
        .filter((timeSet) => timeSet.times.length > 0);
};

/**
 * Decides whether a rule should appear on the experience-view webrule list for
 * the given guest count, using three-tier logic:
 *
 * 1. Text rows → keep unless closed (comment cards, not bookable).
 * 2. No slots for this date (times is empty):
 *    - Range declared (min or max > 0) → keep iff guestCount is in [min||1, max||∞].
 *    - No range → keep (no signal to filter by).
 * 3. Has slots → slot-level filter: keep iff at least one slot accepts guestCount.
 *
 * The returned rule has its `times` reduced to only the slots bookable for
 * guestCount.
 */
const getWebRuleListForGuestCount = (timeSets: WebRule[], guestCount: number): WebRule[] => {
    return timeSets
        .filter((timeSet) => {
            if (timeSet.isTextRow) return !timeSet.isClosed;

            if (timeSet.times.length === 0) {
                const hasRange = timeSet.minGroupSize > 0 || timeSet.maxGroupSize > 0;
                if (!hasRange) return true;
                const min = timeSet.minGroupSize || 1;
                const max = timeSet.maxGroupSize || Infinity;
                return guestCount >= min && guestCount <= max;
            }

            return timeSet.times.some((time) => isTimeBookable(time, guestCount));
        })
        .map(
            (timeSet) =>
                new WebRule({ ...timeSet, times: getBookableTimes(timeSet.times, guestCount) })
        );
};

export {
    isTimeBookable,
    isTimeWaitListable,
    getBookableTimes,
    getWaitListTimes,
    getBookableTimeSets,
    getWaitListTimeSets,
    getWebRuleListForGuestCount,
};
